import { available, isU64, unavailable } from './integer.js';
import type { PoolMathResult } from './types.js';

export const CONVICTION_SCALE_PPB = 1_000_000_000n;
export const MAX_EVENT_CONVICTION_PPB = 2_000_000_000n;

export interface BondingCurveConvictionAnchor {
  /** Current real quote reserves from Market.quote_reserves, one per outcome. */
  realQuoteReservesD6: readonly bigint[];
  /** Stored Market.outcome_conviction_ppb values, one per outcome. */
  outcomeConvictionPpb: readonly bigint[];
  lastConvictionUpdateUnixSeconds: bigint;
  convictionBaselineReserveQuoteD6: bigint;
  convictionRetentionPerSecondPpb: bigint;
}

export interface BondingCurveConvictionState extends BondingCurveConvictionAnchor {
  convictionResolutionThresholdPpb: bigint;
  /** Exact first threshold touch for current-contract quote snapshots. */
  resolutionAtUnixSeconds?: bigint | null | undefined;
  /** Timed markets trade only inside their scheduled window. */
  timedTradingWindow?: { startUnixSeconds: bigint; endUnixSeconds: bigint } | undefined;
}

export interface BondingCurveConvictionAdvance {
  effectiveNowUnixSeconds: bigint;
  updatedOutcomeConvictionPpb: readonly bigint[];
  totalConvictionPpb: bigint;
  leaderOutcomeIndex: number;
  leaderConvictionPpb: bigint;
}

export interface BondingCurveConvictionEvaluation extends BondingCurveConvictionAdvance {
  resolutionWinnerOutcomeIndex: number | null;
}

/**
 * Fixed-threshold fallback for quote contexts without a projected first touch.
 * Current-contract snapshots carry `resolutionAtUnixSeconds` instead.
 */
export function evaluateBondingCurveConviction(
  state: BondingCurveConvictionState,
  nowUnixSeconds: bigint,
): PoolMathResult<BondingCurveConvictionEvaluation> {
  if (
    state.convictionResolutionThresholdPpb <= 0n ||
    state.convictionResolutionThresholdPpb > CONVICTION_SCALE_PPB
  )
    return unavailable('invalid_conviction_state');
  const advanced = advanceBondingCurveConviction(state, nowUnixSeconds);
  if (!advanced.available) return advanced;
  const { leaderConvictionPpb, totalConvictionPpb, leaderOutcomeIndex } = advanced.value;

  const thresholdReached =
    totalConvictionPpb > 0n &&
    leaderConvictionPpb * CONVICTION_SCALE_PPB >=
      state.convictionResolutionThresholdPpb * totalConvictionPpb;

  return available({
    ...advanced.value,
    resolutionWinnerOutcomeIndex: thresholdReached ? leaderOutcomeIndex : null,
  });
}

/** Advances the indexed anchor with contract integer rounding, without an eligibility decision. */
export function advanceBondingCurveConviction(
  state: BondingCurveConvictionAnchor,
  nowUnixSeconds: bigint,
): PoolMathResult<BondingCurveConvictionAdvance> {
  if (!validState(state, nowUnixSeconds)) return unavailable('invalid_conviction_state');

  const effectiveNowUnixSeconds = maximum(nowUnixSeconds, state.lastConvictionUpdateUnixSeconds);
  const elapsed = effectiveNowUnixSeconds - state.lastConvictionUpdateUnixSeconds;
  const retention = fixedPowPpb(state.convictionRetentionPerSecondPpb, elapsed);
  const targets = convictionTargetsPpb(state);
  const updatedOutcomeConvictionPpb = state.outcomeConvictionPpb.map((oldValue, index) =>
    blendConvictionPpb(oldValue, requiredAt(targets, index), retention),
  );

  let leaderOutcomeIndex = 0;
  let leaderConvictionPpb = requiredAt(updatedOutcomeConvictionPpb, 0);
  let totalConvictionPpb = 0n;
  for (let index = 0; index < updatedOutcomeConvictionPpb.length; index += 1) {
    const value = requiredAt(updatedOutcomeConvictionPpb, index);
    totalConvictionPpb += value;
    if (value > leaderConvictionPpb) {
      leaderOutcomeIndex = index;
      leaderConvictionPpb = value;
    }
  }

  return available({
    effectiveNowUnixSeconds,
    updatedOutcomeConvictionPpb,
    totalConvictionPpb,
    leaderOutcomeIndex,
    leaderConvictionPpb,
  });
}

function validState(state: BondingCurveConvictionAnchor, nowUnixSeconds: bigint): boolean {
  const outcomeCount = state.realQuoteReservesD6.length;
  return (
    outcomeCount > 0 &&
    outcomeCount <= 10 &&
    state.outcomeConvictionPpb.length === outcomeCount &&
    state.realQuoteReservesD6.every(isU64) &&
    state.outcomeConvictionPpb.every(isU64) &&
    isU64(nowUnixSeconds) &&
    isU64(state.lastConvictionUpdateUnixSeconds) &&
    isU64(state.convictionBaselineReserveQuoteD6) &&
    state.convictionRetentionPerSecondPpb >= 0n &&
    state.convictionRetentionPerSecondPpb < CONVICTION_SCALE_PPB
  );
}

function convictionTargetsPpb(state: BondingCurveConvictionAnchor): readonly bigint[] {
  const counted = state.realQuoteReservesD6.map(
    (reserve) => reserve + state.convictionBaselineReserveQuoteD6,
  );
  const total = counted.reduce((sum, value) => sum + value, 0n);
  if (total === 0n) {
    const equalShare = CONVICTION_SCALE_PPB / BigInt(counted.length);
    return counted.map(() => equalShare);
  }
  return counted.map((value) => (value * CONVICTION_SCALE_PPB) / total);
}

/** Exact PPB multiply used by the on-chain conviction recurrence. */
export function fixedMulPpb(left: bigint, right: bigint): bigint {
  return (left * right) / CONVICTION_SCALE_PPB;
}

/** Exact exponentiation-by-squaring used by the on-chain conviction recurrence. */
export function fixedPowPpb(baseInput: bigint, exponentInput: bigint): bigint {
  let base = baseInput;
  let exponent = exponentInput;
  let result = CONVICTION_SCALE_PPB;
  while (exponent > 0n) {
    if ((exponent & 1n) === 1n) result = fixedMulPpb(result, base);
    exponent >>= 1n;
    if (exponent > 0n) base = fixedMulPpb(base, base);
  }
  return result;
}

/** Exact conviction blend, including the contract's saturating retention complement. */
export function blendConvictionPpb(
  oldValue: bigint,
  targetValue: bigint,
  retention: bigint,
): bigint {
  const retained = oldValue * retention;
  const complement = retention < CONVICTION_SCALE_PPB ? CONVICTION_SCALE_PPB - retention : 0n;
  const absorbed = targetValue * complement;
  return (retained + absorbed) / CONVICTION_SCALE_PPB;
}

function maximum(left: bigint, right: bigint): bigint {
  return left > right ? left : right;
}

function requiredAt(values: readonly bigint[], index: number): bigint {
  const value = values[index];
  if (value === undefined) throw new Error('Validated conviction vector is incomplete');
  return value;
}
